import { ProspectShell } from '../../components/prospect/ProspectShell';

// Plain-language data-handling page. Doubles as sales collateral for the
// security-conscious admin (the actual buyer), so: no legalese.
export function ProspectPrivacyPage() {
  return (
    <ProspectShell>
      <div className="max-w-[680px] mx-auto pt-14">
        <h1 className="text-[40px] font-bold tracking-[-0.04em] leading-tight mb-3">
          How OrgIQ handles your data
        </h1>
        <p className="text-sm text-neutral-400 leading-relaxed mb-10">
          Short version: the scan reads configuration, counts, and a few samples. It keeps
          counts, scores, configuration names and activity rows without names, plus any AI report
          written from them. Here is exactly what happens when you connect a Salesforce
          org. Last updated 23 September 2026.
        </p>

        <Section title="Read requests only">
          OrgIQ authenticates with standard Salesforce OAuth. You log in on
          Salesforce&apos;s own pages and we never see your password. Salesforce has no
          read-only permission for apps, so its approval screen says &ldquo;Manage user data
          via APIs&rdquo;. OrgIQ sends read requests only. To enforce that on your side, log
          in with a user that has a read-only profile.
        </Section>

        <Section title="What the scan reads">
          Configuration: objects, fields, flows, Apex, packages, profiles and permission
          sets. Field definitions, record types, page layouts, sharing settings and sharing
          rules, approval processes, assignment, auto-response and escalation rules, Apex test
          coverage, the API versions of your code, and which permission sets and profiles hold
          powerful permissions and how many active users have them. Integrations: named
          credentials, remote sites, outbound messages, platform events, Experience sites, and
          the connected apps your users have authorised, counted per app. Record counts, storage limits, and the size and owner of your largest files. Your
          user list and 90 days of login history, to count active and idle licences. Recent
          flow errors. A sample of up to 5,000 email fields on Contact and Lead, and a small
          sample of Account phone and address fields, to measure data quality.
        </Section>

        <Section title="What happens to it">
          The field samples and the login history are processed in memory, turned into counts
          and percentages, then dropped. Names, usernames and email addresses of your people,
          file names and flow interview labels are stripped before anything is stored.
        </Section>

        <Section title="One scan, then access is revoked">
          We ask Salesforce for access without a refresh token, and we ask Salesforce
          to revoke that access when the scan finishes. We store no Salesforce password or token. A rescan needs a
          new login from you. You can also block the app at any time in Setup, Connected
          Apps OAuth Usage.
        </Section>

        <Section title="What we keep, and for how long">
          Your scan results: counts, scores, and configuration names such as object, flow
          and profile names. Per-user activity rows without names: Salesforce user ID,
          profile, licence, last login date and login-day counts. The details on your invite:
          name, email, company. The record of your approval: terms version, time and IP
          address, the email the invite was sent to, and, if the scan was approved in
          Salesforce, the Salesforce username and org ID of the person who approved it.
          TYRL can also generate AI reports from those
          results, and a report that is generated is stored with your scan and deleted with it.
          Scan results are deleted
          after 6 months. You can delete them sooner with the button on your results page.
          TYRL can also delete your record sooner: the scan results, any AI report, and the
          name and company on your invite go. We keep the approval record as evidence that
          the scan was authorised. A record that was never approved has no approval record,
          so all of it goes, the email included. If you click a button to book a call, we keep
          the time of your last click on your scan record, or on your Hub user if you click it
          in the Hub. TYRL can download your scan results as an Excel file to prepare for a
          call. The file holds the same results we store, with no names of your people.
        </Section>

        <Section title="Who else receives data">
          Anthropic (Claude API) receives the aggregate results to write the AI reports: counts,
          scores and their rating labels, your email deliverability setting, your org name,
          your Salesforce edition, and whether you own Service Cloud or
          Sales Cloud seats. It also receives object names: the objects a finding is about, and
          the standard objects the data quality checks look at. It also receives these counts:
          Apex test coverage, code by API version, objects by sharing setting, the number of
          sharing rules, how many permission sets and profiles hold Modify All Data or View All
          Data, integration counts, and approval processes and rules by active or inactive.
          It receives no record values,
          and no names or email addresses of your people. Resend delivers our
          emails: the ones to your address, and the internal copy of your scan results
          that TYRL receives. Your contact details go to no marketing system.
        </Section>

        <Section title="What we do not have">
          TYRL is a small agency. We hold no SOC 2 or ISO 27001 certificate and have had no
          independent penetration test. If your policy needs these, run the scan on a
          sandbox, use a read-only user, or ask us for a screen-share scan.
        </Section>

        <Section title="Scan terms">
          The scan and its results are free and are general information, not professional
          advice. You confirm that you are authorised to approve the scan for your
          organisation. You remain responsible for the security of your Salesforce org, your
          users and your data. To the extent the law permits, TYRL is not liable for any
          loss arising from the scan or from reliance on its results, including loss of
          data, security incidents in your systems, and indirect or consequential loss, and
          TYRL&apos;s total liability is limited to AUD 100. Nothing in these terms excludes
          rights you have under the Australian Consumer Law that cannot be excluded.
        </Section>

        <Section title="Questions">
          Ask us anything before you connect. Reply to the email that brought you here.
          TYRL · tyrl.ai
        </Section>
      </div>
    </ProspectShell>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-base font-semibold mb-2">{title}</h2>
      <p className="text-sm text-neutral-400 leading-relaxed">{children}</p>
    </section>
  );
}
